/**
 * usePmsPrefill — create-session state for the PMS patient lookup + prefill
 * flow on the Knipper create-case page (AQ-580 v0).
 *
 * Implements the v0 slice of the autofill-vs-suggestion framework
 * (tech design §6, normative):
 *   - AUTOFILL policy commits only when exactly ONE value exists
 *   - N>1 never commits (options surface as an informational hint in v0)
 *   - OFFER policy feeds the existing SuggestionChip accept flow
 *   - reporter-block values wait for the who-is-calling answer (R2)
 *   - provenance: pms_sourced_fields = autofilled + accepted only;
 *     an edited field is no longer PMS-verified
 *
 * All PMS state is client-side session state — nothing persists except the
 * reserved input_data keys attached at submit (decision D4).
 */
import type {
  GetPmsPatientPrefillResponse,
  PmsCandidate,
  PmsFieldValues,
  PmsResultState,
} from '@compass/client/types/pms';
import { PmsFillPolicy, PmsGapReason } from '@compass/client/types/pms';
import { pmsService } from '@services/api';
import { useCallback, useMemo, useRef, useState } from 'react';

export type PmsFieldState =
  | 'autofilled'
  | 'suggested'
  | 'accepted'
  | 'ignored'
  | 'edited'
  | 'cleared';

type PmsGapBadge = 'ask' | 'not_in_pms';

export type PmsReporterRole = 'patient' | 'hcp' | 'caregiver' | 'other';

interface PmsOfferOption {
  value: string;
  /** Data-derived label, e.g. "HOME · default ship" — never generic. */
  label?: string;
}

interface PmsOffer {
  /** Single-value offers carry the value for the accept chip. */
  suggestion?: string;
  /**
   * Multi-value offers carry structured options for the "PMS returned N
   * values — choose one" chooser. Never pre-committed, whatever the policy.
   */
  options?: PmsOfferOption[];
}

/** Provenance/gap badge rendered beside the field label. */
interface PmsFieldBadge {
  kind: 'pms' | 'edited' | PmsGapBadge;
  label: string;
}

/**
 * R4 ("never a blank without a reason") + provenance in one place:
 * PMS-verified values badge `PMS`, diverged values badge `EDITED`, and an
 * empty mapped field explains itself (`ASK` / `NOT IN PMS`) — but a gap badge
 * disappears once the agent has typed a value, because the field is no
 * longer blank.
 */
export function deriveFieldBadge(
  state: PmsFieldState | undefined,
  gap: PmsGapBadge | undefined,
  hasValue: boolean,
): PmsFieldBadge | undefined {
  if (state === 'autofilled' || state === 'accepted') return { kind: 'pms', label: 'PMS' };
  if (state === 'edited') return { kind: 'edited', label: 'EDITED' };
  if (!hasValue && gap === 'ask') return { kind: 'ask', label: 'ASK' };
  if (!hasValue && gap === 'not_in_pms') return { kind: 'not_in_pms', label: 'NOT IN PMS' };
  return undefined;
}

export interface PmsFieldPlan {
  /** field id → value to commit (AUTOFILL policy, exactly one value). */
  autofill: Record<string, string>;
  /** field id → offer (OFFER policy, or AUTOFILL with N>1 which never commits). */
  offers: Record<string, PmsOffer>;
  /** Mapped fields with no PMS value. */
  gaps: Record<string, PmsGapBadge>;
  /** Reporter-block fields withheld until the who-is-calling answer. */
  awaitingRole: string[];
}

/**
 * Pure core of the §6 decision tree, evaluated against the prefill payload
 * and the current reporter role. Policy comes from the server; this function
 * never re-derives it.
 */
export function derivePmsFieldPlan(
  fieldValues: Record<string, PmsFieldValues>,
  gaps: Record<string, number>,
  reporterRole: PmsReporterRole | null,
): PmsFieldPlan {
  const plan: PmsFieldPlan = { autofill: {}, offers: {}, gaps: {}, awaitingRole: [] };

  Object.entries(fieldValues).forEach(([fieldId, fv]) => {
    let values = fv.values ?? [];

    if (fv.cascade_group === 'reporter') {
      if (!reporterRole) {
        plan.awaitingRole.push(fieldId);
        return;
      }
      values = values.filter(v => !v.role_class || v.role_class === reporterRole);
      if (values.length === 0) return;
    }
    if (values.length === 0) return;

    if (fv.policy === PmsFillPolicy.PMS_FILL_POLICY_AUTOFILL && values.length === 1) {
      plan.autofill[fieldId] = values[0].value ?? '';
      return;
    }

    if (values.length === 1) {
      plan.offers[fieldId] = { suggestion: values[0].value };
      return;
    }

    // N>1 — never pre-commit, whatever the policy (tightened vs the demo).
    // Structured options feed the chooser; the agent picks, never re-types.
    plan.offers[fieldId] = {
      options: values
        .filter(v => v.value)
        .map(v => ({ value: v.value as string, ...(v.label ? { label: v.label } : {}) })),
    };
  });

  Object.entries(gaps).forEach(([fieldId, reason]) => {
    plan.gaps[fieldId] = reason === PmsGapReason.PMS_GAP_REASON_ASK ? 'ask' : 'not_in_pms';
  });

  return plan;
}

/**
 * Match a PMS value onto a select/radio field's option values (the §6.6
 * catalog-match rule applied to enum fields): exact case-insensitive match,
 * then prefix match either way ("F" → "female"). Returns the option value to
 * commit, or undefined — in which case the field must stay empty (never
 * commit a value the control can't represent).
 */
export function matchEnumOption(
  value: string,
  options: Array<{ value: string | boolean | number; label?: string }>,
): string | boolean | number | undefined {
  const v = value.trim().toLowerCase();
  if (!v) return undefined;
  const exact = options.find(
    o => String(o.value).toLowerCase() === v || (o.label ?? '').toLowerCase() === v,
  );
  if (exact) return exact.value;
  const prefix = options.find(o => {
    const ov = String(o.value).toLowerCase();
    const ol = (o.label ?? '').toLowerCase();
    return ov.startsWith(v) || v.startsWith(ov) || ol.startsWith(v);
  });
  return prefix?.value;
}

/**
 * Classify a schema reporter-role option value into a PMS role class —
 * the reverse direction of the gate→field sync. Mirrors the mapping's
 * role_classes convention. Returns null for unrecognizable values.
 */
export function classifyReporterRole(optionValue: string): PmsReporterRole | null {
  const v = optionValue.trim().toLowerCase();
  if (!v) return null;
  if (/patient|self/.test(v)) return 'patient';
  if (/hcp|doctor|nurse|physician|pharmacist|health/.test(v)) return 'hcp';
  if (/caregiver|family|friend|spouse|parent|relative/.test(v)) return 'caregiver';
  if (/other/.test(v)) return 'other';
  return null;
}

/** pms_sourced_fields per §6.4: AUTOFILLED + ACCEPTED only. */
export function pmsSourcedFields(states: Record<string, PmsFieldState>): string[] {
  return Object.entries(states)
    .filter(([, s]) => s === 'autofilled' || s === 'accepted')
    .map(([fieldId]) => fieldId);
}

/** Reserved input_data keys attached at submit (decision D4). */
export function buildPmsSubmitKeys(
  states: Record<string, PmsFieldState>,
  flags: string[],
): { pms_sourced_fields?: string[]; pms_flags?: string[] } {
  const sourced = pmsSourcedFields(states);
  const keys: { pms_sourced_fields?: string[]; pms_flags?: string[] } = {};
  if (sourced.length > 0) keys.pms_sourced_fields = sourced;
  if (flags.length > 0) keys.pms_flags = flags;
  return keys;
}

export interface UsePmsPrefillArgs {
  organizationId: string | undefined;
  programId: string | undefined;
  workflowId: string | undefined;
  /** The pms_enabled workflow feature flag — nothing runs without it. */
  enabled: boolean;
}

export interface PmsSearchInput {
  lastName: string;
  firstName?: string;
  dob?: string;
  patientId?: string;
}

export type PmsStatus = 'idle' | 'searching' | 'loading_prefill' | 'loaded' | 'error';

export function usePmsPrefill({
  organizationId,
  programId,
  workflowId,
  enabled,
}: UsePmsPrefillArgs) {
  const [status, setStatus] = useState<PmsStatus>('idle');
  const [candidates, setCandidates] = useState<PmsCandidate[]>([]);
  const [resultState, setResultState] = useState<PmsResultState | undefined>(undefined);
  const [totalMatches, setTotalMatches] = useState(0);
  const [nextPageToken, setNextPageToken] = useState('');
  const [confirmedPatient, setConfirmedPatient] = useState<PmsCandidate | null>(null);
  const [prefill, setPrefill] = useState<GetPmsPatientPrefillResponse | null>(null);
  const [reporterRole, setReporterRole] = useState<PmsReporterRole | null>(null);
  const [fieldStates, setFieldStates] = useState<Record<string, PmsFieldState>>({});
  /** field id → the value the PMS committed (autofill/accept) — restore source. */
  const [originals, setOriginals] = useState<Record<string, string>>({});
  const [flags, setFlags] = useState<string[]>([]);
  const lastSearch = useRef<PmsSearchInput | null>(null);

  const addFlag = useCallback((flag: string) => {
    setFlags(prev => (prev.includes(flag) ? prev : [...prev, flag]));
  }, []);

  const search = useCallback(
    async (input: PmsSearchInput, pageToken = '') => {
      if (!enabled || !organizationId || !programId || !workflowId) return;
      lastSearch.current = input;
      setStatus('searching');
      try {
        const resp = await pmsService.searchPmsPatients({
          organization_id: organizationId,
          program_id: programId,
          workflow_id: workflowId,
          last_name: input.lastName,
          first_name: input.firstName,
          dob: input.dob,
          patient_id: input.patientId,
          page_size: 10,
          page_token: pageToken,
        });
        setCandidates(prev =>
          pageToken ? [...prev, ...(resp.candidates ?? [])] : (resp.candidates ?? []),
        );
        setResultState(resp.result_state);
        setTotalMatches(resp.total_matches ?? 0);
        setNextPageToken(resp.next_page_token ?? '');
        setStatus('idle');
      } catch {
        // Degraded mode: the form must keep working without PMS.
        setStatus('error');
        addFlag('pms_unavailable_during_intake');
      }
    },
    [enabled, organizationId, programId, workflowId, addFlag],
  );

  const showMore = useCallback(() => {
    // Guard against double-fire while a page is already in flight — the same
    // token would append the same page twice (duplicate candidates + keys).
    if (status !== 'searching' && lastSearch.current && nextPageToken) {
      void search(lastSearch.current, nextPageToken);
    }
  }, [search, nextPageToken, status]);

  const confirmPatient = useCallback(
    async (candidate: PmsCandidate) => {
      if (!enabled || !organizationId || !programId || !workflowId || !candidate.mfi) return;
      setConfirmedPatient(candidate);
      setStatus('loading_prefill');
      try {
        const resp = await pmsService.getPmsPatientPrefill({
          organization_id: organizationId,
          program_id: programId,
          workflow_id: workflowId,
          mfi: candidate.mfi,
          epost_member_id: candidate.epost_member_id,
        });
        setPrefill(resp);
        setStatus('loaded');
      } catch {
        setStatus('error');
        addFlag('pms_unavailable_during_intake');
      }
    },
    [enabled, organizationId, programId, workflowId, addFlag],
  );

  const plan = useMemo(
    () => derivePmsFieldPlan(prefill?.field_values ?? {}, prefill?.gaps ?? {}, reporterRole),
    [prefill, reporterRole],
  );

  /** Field ids in the reporter cascade group (role-dependent values). */
  const reporterFieldIds = useMemo(
    () =>
      Object.entries(prefill?.field_values ?? {})
        .filter(([, fv]) => fv.cascade_group === 'reporter')
        .map(([id]) => id),
    [prefill],
  );

  /**
   * Role change re-points the reporter block (R9): PMS-autofilled reporter
   * values that the agent has NOT edited are cleared (their states drop) so
   * the new role's values re-apply through the normal autofill path. Returns
   * the field ids the caller must blank in the form state.
   */
  const changeReporterRole = useCallback(
    (role: PmsReporterRole): string[] => {
      const toClear = reporterFieldIds.filter(id => fieldStates[id] === 'autofilled');
      if (toClear.length > 0) {
        setFieldStates(prev => {
          const next = { ...prev };
          toClear.forEach(id => delete next[id]);
          return next;
        });
        setOriginals(prev => {
          const next = { ...prev };
          toClear.forEach(id => delete next[id]);
          return next;
        });
      }
      setReporterRole(role);
      return toClear;
    },
    [reporterFieldIds, fieldStates],
  );

  const markAutofilled = useCallback((values: Record<string, string>) => {
    setFieldStates(prev => {
      const next = { ...prev };
      Object.keys(values).forEach(id => {
        if (next[id] === undefined) next[id] = 'autofilled';
      });
      return next;
    });
    // Remember what the PMS put there, so an EDITED field can be restored.
    setOriginals(prev => ({ ...values, ...prev }));
  }, []);

  const markAccepted = useCallback((fieldId: string, value: string) => {
    setFieldStates(prev => ({ ...prev, [fieldId]: 'accepted' }));
    setOriginals(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  /**
   * Restore an EDITED field to the value the PMS originally supplied.
   * Returns that value (the caller writes it back into the form) — the field
   * becomes PMS-verified again ('accepted': restoring is a deliberate act).
   */
  const restoreField = useCallback(
    (fieldId: string): string | undefined => {
      const original = originals[fieldId];
      if (original === undefined) return undefined;
      setFieldStates(prev => ({ ...prev, [fieldId]: 'accepted' }));
      return original;
    },
    [originals],
  );

  const markIgnored = useCallback((fieldId: string) => {
    setFieldStates(prev => ({ ...prev, [fieldId]: 'ignored' }));
  }, []);

  /** Call on user edits: a changed PMS value is no longer PMS-verified. */
  const markFieldEdited = useCallback((fieldId: string) => {
    setFieldStates(prev => {
      const current = prev[fieldId];
      if (current === 'autofilled' || current === 'accepted') {
        return { ...prev, [fieldId]: 'edited' };
      }
      return prev;
    });
  }, []);

  const proceedWithoutPms = useCallback(() => {
    addFlag('no_pms_record');
    setCandidates([]);
    setResultState(undefined);
  }, [addFlag]);

  const submitKeys = useCallback(
    () => buildPmsSubmitKeys(fieldStates, flags),
    [fieldStates, flags],
  );

  /**
   * Full teardown ("Change patient" / start over). Clears all hook state and
   * returns the field ids whose form values came from PMS (autofilled or
   * accepted) — the caller MUST blank those in its form state, otherwise the
   * previous patient's data silently survives into the next patient's case.
   * Edited values are NOT returned: the agent typed over them and owns them.
   */
  const reset = useCallback((): string[] => {
    const toClear = pmsSourcedFields(fieldStates);
    setStatus('idle');
    setCandidates([]);
    setResultState(undefined);
    setTotalMatches(0);
    setNextPageToken('');
    setConfirmedPatient(null);
    setPrefill(null);
    setReporterRole(null);
    setFieldStates({});
    setOriginals({});
    setFlags([]);
    return toClear;
  }, [fieldStates]);

  return {
    enabled,
    fieldStates,
    status,
    candidates,
    resultState,
    totalMatches,
    nextPageToken,
    confirmedPatient,
    prefill,
    plan,
    reporterRole,
    setReporterRole,
    changeReporterRole,
    reporterFieldIds,
    search,
    showMore,
    confirmPatient,
    markAutofilled,
    markAccepted,
    markIgnored,
    markFieldEdited,
    restoreField,
    proceedWithoutPms,
    addFlag,
    submitKeys,
    reset,
  };
}
